/**
 * TreeLevelControls — 진도 목록 찾기 칸 오른쪽의 [− N단 +]와 전체 펼치기/접기(오너 C안 2026-11-02,
 * 시안 docs/learning-composition-v2/assets/tree-toolbar-20261102/proposal.png).
 *
 * 펼침은 보기 상태라 서버로 가지 않는다 — 부르는 쪽이 받은 집합을 그대로 상태·세션에 둔다(openState).
 * 찾는 동안은 나무가 맞은 행의 조상을 저절로 펼쳐 이 값이 화면에 듣지 않으므로 잠근다(거짓 반응을 내지 않는다).
 * 접을 하위가 있는 줄이 없으면(줄 0개 · 전부 최상위) 아무것도 그리지 않는다.
 */
import { useMemo } from 'react';
import {
  ChevronsDownUp, ChevronsUpDown, Minus, Plus,
} from 'lucide-react';
import {
  collapseOneLevel, expandOneLevel, idsToLevel, openLevelOf,
} from './treeIndex';

const ringCls = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary';
const iconBtnCls = `inline-flex items-center justify-center text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent ${ringCls}`;
const LOCKED = '찾는 동안에는 쓸 수 없습니다';

export default function TreeLevelControls({ index, expanded, onChange, disabled = false }) {
  const { level, visible, maxDepth } = useMemo(() => openLevelOf(index, expanded), [index, expanded]);
  if (maxDepth < 2) return null;

  const allOpen = level >= maxDepth;
  const canDown = !disabled && visible > 1;
  const canUp = !disabled && !allOpen;
  const toggleLabel = allOpen ? '전체 접기' : '전체 펼치기';

  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <div role="group" aria-label="진도 목록 펼침 단계" className="flex h-7 items-center gap-px rounded-lg border border-gray-200 bg-white px-0.5">
        <button
          type="button"
          disabled={!canDown}
          onClick={() => onChange(collapseOneLevel(index, expanded))}
          aria-label="한 단 접기"
          title={disabled ? LOCKED : '한 단 접기'}
          className={`h-6 w-[22px] rounded-md ${iconBtnCls}`}
        >
          <Minus aria-hidden="true" className="size-3.5" />
        </button>
        <span aria-live="polite" className={`min-w-[22px] text-center text-xs font-bold ${disabled ? 'text-gray-300' : 'text-primary'}`}>
          {level}단
        </span>
        <button
          type="button"
          disabled={!canUp}
          onClick={() => onChange(expandOneLevel(index, expanded))}
          aria-label="한 단 더 펼치기"
          title={disabled ? LOCKED : '한 단 더 펼치기'}
          className={`h-6 w-[22px] rounded-md ${iconBtnCls}`}
        >
          <Plus aria-hidden="true" className="size-3.5" />
        </button>
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(allOpen ? new Set() : idsToLevel(index, Infinity))}
        aria-label={toggleLabel}
        title={disabled ? LOCKED : toggleLabel}
        className={`size-7 rounded-lg border border-gray-200 bg-white ${iconBtnCls}`}
      >
        {allOpen
          ? <ChevronsDownUp aria-hidden="true" className="size-3.5" />
          : <ChevronsUpDown aria-hidden="true" className="size-3.5" />}
      </button>
    </div>
  );
}
